import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TeamRegistrationForm, { TeamRegistrationData } from '../components/TeamRegistrationForm';
import { useApiClient } from '../services/api';
import BackButton from '../components/BackButton';
import CustomLoader from '../components/CustomLoader';

interface Event {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  startDate: Date | string;
  endDate: Date | string;
}

const TeamRegistrationPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();

  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [unregistering, setUnregistering] = useState(false);

  useEffect(() => {
    const fetchEventData = async () => {
      if (!eventId) return;

      try {
        setLoading(true);
        // Fetch event details
        const response = await apiClient.getCurrentEventsBountiesWithParticipation();

        if (response.success && response.data) {
          const foundEvent = response.data.events.find((e: any) => e.id === eventId);

          if (foundEvent) {
            setEvent({
              id: foundEvent.id,
              title: foundEvent.title,
              description: foundEvent.description,
              category: foundEvent.theme,
              status: foundEvent.status,
              startDate: foundEvent.startDate,
              endDate: foundEvent.endDate,
            });

            // Check if user already has a team (for edit mode)
            const teamCheckResponse = await apiClient.checkUserTeamForEvent(eventId);
            if (teamCheckResponse.success && teamCheckResponse.data?.hasTeam && teamCheckResponse.data?.team) {
              setIsEditMode(true);
              setTeamId(teamCheckResponse.data.team.id);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching event data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [eventId, apiClient]);

  const handleSubmit = async (teamData: TeamRegistrationData) => {
    setSubmitting(true);

    try {
      let response;

      if (isEditMode && teamId) {
        // Update existing team
        response = await apiClient.updateTeamRegistration(teamId, {
          teamName: teamData.teamName,
          lookingForSkills: teamData.lookingForSkills,
          teamMemberEmails: teamData.teamMemberEmails,
        });
      } else {
        // Create new team
        response = await apiClient.createTeamRegistration(teamData);
      }

      if (!response.success) {
        throw new Error(response.message || `Team ${isEditMode ? 'update' : 'registration'} failed`);
      }

      // Invalidate specific caches
      apiClient.invalidateCache('current-events-bounties');
      apiClient.invalidateCache('with-participation');
      apiClient.invalidateCache('user-events-bounties');
      apiClient.invalidateCache('teams');
      apiClient.invalidateCache('team-registration');
      apiClient.invalidateCache('hackdays');

      // Navigate back to events page with state to indicate refresh needed
      navigate('/events', { replace: true, state: { refresh: true } });
    } catch (err) {
      console.error('Team operation failed:', err);
      throw err; // Re-throw to let form handle it
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/events', { replace: true });
  };

  const handleUnregister = async () => {
    if (!eventId) return;

    setUnregistering(true);
    try {
      const response = await apiClient.resignFromEvent(eventId);

      if (response.success) {
        // Invalidate caches
        apiClient.invalidateCache('current-events-bounties');
        apiClient.invalidateCache('with-participation');
        apiClient.invalidateCache('user-events-bounties');

        // Navigate back to events page with state to indicate refresh needed
        navigate('/events', { replace: true, state: { refresh: true } });
      } else {
        console.error('Failed to unregister from event:', response.error);
      }
    } catch (err) {
      console.error('Error unregistering from event:', err);
    } finally {
      setUnregistering(false);
    }
  };

  if (loading) {
    return <CustomLoader />;
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Event Not Found</h2>
          <BackButton />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button: push down 35px to avoid being blocked by navbar and sit clearly above the section */}
        <div style={{ marginTop: 35, marginBottom: 8 }}>
          <BackButton />
        </div>

        {/* Form */}
        <TeamRegistrationForm
          eventId={event.id}
          eventTitle={event.title}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={submitting}
          isEditMode={isEditMode}
          teamId={teamId || undefined}
          onUnregister={isEditMode ? handleUnregister : undefined}
          isUnregistering={unregistering}
          isFullPage={true}
        />
      </div>
    </div>
  );
};

export default TeamRegistrationPage;
